import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Fighter, FighterFight } from '@/types'
import { fighters as fightersData } from '@/data/fighters'
import { events as eventsData } from '@/data/events'
import { api } from '@/api'
import { getRealtimeManager, type RealtimeMessage } from '@/composables/useRealtime'

export const useFightersStore = defineStore('fighters', () => {
  const fighters = ref<Fighter[]>(fightersData)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastUpdated = ref<number | null>(null)
  const isRealtimeConnected = ref(false)

  // Real-time subscription cleanup
  let unsubscribeFighters: (() => void) | null = null
  let unsubscribeFighterUpdate: (() => void) | null = null
  let unsubscribeStateChange: (() => void) | null = null

  const weightClasses = computed(() => {
    const classes = new Set(fighters.value.map(f => f.weightClass))
    return Array.from(classes)
  })

  async function fetchFighters(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const data = await api.get<Fighter[]>('/fighters')
      fighters.value = data
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch fighters'
      error.value = message
      // Keep existing data on error (static fallback)
    } finally {
      isLoading.value = false
    }
  }

  function getFighterById(id: number): Fighter | undefined {
    return fighters.value.find(f => f.id === id)
  }

  async function fetchFighterById(id: number): Promise<Fighter | undefined> {
    isLoading.value = true
    error.value = null

    try {
      const fighter = await api.get<Fighter>(`/fighters/${id}`)
      // Update the fighter in the local array if it exists
      const index = fighters.value.findIndex(f => f.id === id)
      if (index >= 0) {
        fighters.value[index] = fighter
      } else {
        fighters.value.push(fighter)
      }
      return fighter
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch fighter'
      error.value = message
      // Return from static data on error
      return getFighterById(id)
    } finally {
      isLoading.value = false
    }
  }

  function getFightersByWeightClass(weightClass: string): Fighter[] {
    return fighters.value.filter(f => f.weightClass === weightClass)
  }

  function clearError(): void {
    error.value = null
  }

  /**
   * Subscribe to real-time fighter updates
   */
  function subscribeToRealtime(): void {
    const manager = getRealtimeManager()

    // Subscribe to bulk fighters updates (from polling fallback)
    unsubscribeFighters = manager.on<Fighter[]>('fighters_update', (message: RealtimeMessage<Fighter[]>) => {
      fighters.value = message.data
      lastUpdated.value = message.timestamp || Date.now()
    })

    // Subscribe to individual fighter updates (from SSE/WebSocket)
    unsubscribeFighterUpdate = manager.on<Fighter>('fighter_update', (message: RealtimeMessage<Fighter>) => {
      const updatedFighter = message.data
      const index = fighters.value.findIndex(f => f.id === updatedFighter.id)
      if (index >= 0) {
        fighters.value[index] = updatedFighter
      } else {
        fighters.value.push(updatedFighter)
      }
      lastUpdated.value = message.timestamp || Date.now()
    })

    // Track connection state
    unsubscribeStateChange = manager.onStateChange((state) => {
      isRealtimeConnected.value = state === 'connected'
    })
  }

  /**
   * Unsubscribe from real-time updates
   */
  function unsubscribeFromRealtime(): void {
    unsubscribeFighters?.()
    unsubscribeFighterUpdate?.()
    unsubscribeStateChange?.()
    unsubscribeFighters = null
    unsubscribeFighterUpdate = null
    unsubscribeStateChange = null
  }

  /**
   * Update a single fighter in the store (for optimistic updates)
   */
  function updateFighter(fighter: Fighter): void {
    const index = fighters.value.findIndex(f => f.id === fighter.id)
    if (index >= 0) {
      fighters.value[index] = fighter
    } else {
      fighters.value.push(fighter)
    }
    lastUpdated.value = Date.now()
  }

  function getFighterFightHistory(fighterName: string, limit: number = 5): FighterFight[] {
    const fightHistory: FighterFight[] = []

    for (const event of eventsData) {
      for (const fight of event.fights) {
        const isFighter1 = fight.fighter1 === fighterName
        const isFighter2 = fight.fighter2 === fighterName

        if (isFighter1 || isFighter2) {
          let result: FighterFight['result'] = null
          if (fight.result) {
            if (fight.result.winner === 'draw') {
              result = 'D'
            } else if (fight.result.winner === 'no_contest') {
              result = 'NC'
            } else if (
              (isFighter1 && fight.result.winner === 'fighter1') ||
              (isFighter2 && fight.result.winner === 'fighter2')
            ) {
              result = 'W'
            } else {
              result = 'L'
            }
          }

          fightHistory.push({
            fightId: fight.id,
            eventId: event.id,
            eventName: event.name,
            date: event.date,
            opponentName: isFighter1 ? fight.fighter2 : fight.fighter1,
            result,
            method: fight.result?.method || null,
            round: fight.result?.round || null,
            time: fight.result?.time || null,
            weightClass: fight.weightClass,
            isMainEvent: fight.isMainEvent,
            isTitleFight: fight.isTitleFight
          })
        }
      }
    }

    // Sort by date descending (most recent first)
    fightHistory.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

    return fightHistory.slice(0, limit)
  }

  return {
    // State
    fighters,
    isLoading,
    error,
    lastUpdated,
    isRealtimeConnected,
    // Getters
    weightClasses,
    // Actions
    fetchFighters,
    fetchFighterById,
    getFighterById,
    getFightersByWeightClass,
    clearError,
    getFighterFightHistory,
    // Real-time
    subscribeToRealtime,
    unsubscribeFromRealtime,
    updateFighter
  }
})
